import React, { useState } from 'react';
import { Add as AddIcon } from '@mui/icons-material';
import { CircularProgress, Button } from '@mui/material';
import clsx from 'clsx';

import { SchedulerRoutineItem } from '../SchedulerRoutineItem';
import { RoutinePrefItemState } from '../reducer';
import {
  FirestoreRoutineActivity,
  selectedDateAtom,
  transformHHmmToDayjs,
  translateMinToHHhMMmString,
} from '../../../../data-access';
import { CalendarEvent } from '@demind-inc/core';
import './SchedulerRoutineChatPref.scss';
import { useGeneralSettings } from '../../../../hooks';
import { formatTimeFromDayjs, formatTimeFromHHMM } from '../../../../helpers';
import { RoutineInfoModal } from '../../common';
import { RoutineActivity } from '@demind-inc/core';
import { useRecoilValue } from 'recoil';
import dayjs from 'dayjs';

interface SchedulerRoutineChatPrefProps {
  routine: RoutinePrefItemState;
  isLoading?: boolean;
  onRoutineCheckChange: (activityId: string, checked: boolean) => void;
  onRoutineAdd: (pref: FirestoreRoutineActivity) => void;
  onRoutineChange: (activityId: string, pref: FirestoreRoutineActivity) => void;
  onRoutineDelete: (activityId: string) => void;
  hasConfigured: boolean;
  className?: string;
  fontSize?: 'small' | 'medium' | 'large';
  existingRoutineEvents: CalendarEvent[];
  scheduledRoutineActivityIds: Set<string>;
}

const SchedulerRoutineChatPref: React.FC<SchedulerRoutineChatPrefProps> = ({
  routine,
  isLoading,
  onRoutineCheckChange,
  onRoutineAdd,
  onRoutineChange,
  onRoutineDelete,
  hasConfigured,
  className,
  fontSize = 'medium',
  existingRoutineEvents,
  scheduledRoutineActivityIds,
}) => {
  const {
    generalSettings: { timeFormat },
  } = useGeneralSettings();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRoutineItem, setEditingRoutineItem] = useState<RoutineActivity | null>(null);
  const [routineInfoMode, setRoutineInfoMode] = useState<'edit' | 'create'>('create');
  const [editingRoutineIsAlreadyScheduled, setEditingRoutineIsAlreadyScheduled] = useState(false);

  const selectedDate = useRecoilValue(selectedDateAtom);

  const handleClickRoutineActivity = (
    activity: RoutineActivity & { isAlreadyScheduled?: boolean }
  ) => {
    setEditingRoutineIsAlreadyScheduled(activity.isAlreadyScheduled);
    setEditingRoutineItem(activity);
    setRoutineInfoMode('edit');
    setIsModalOpen(true);
  };

  const handleAddRoutineActivity = () => {
    setRoutineInfoMode('create');
    setIsModalOpen(true);
  };

  const handleRoutineItemChange = ({
    id,
    activity,
  }: {
    id?: string;
    activity: FirestoreRoutineActivity;
  }) => {
    if (id) {
      onRoutineChange(id, activity);
    } else {
      onRoutineAdd(activity);
    }
    setIsModalOpen(false);
  };

  return (
    <div
      className={clsx('scheduler-routine-chat-pref', className, {
        'scheduler-routine-chat-pref--small': fontSize === 'small',
      })}
    >
      <div className="scheduler-routine-chat-pref__list">
        {isLoading ? (
          <div className="scheduler-routine-chat-pref__loading">
            <CircularProgress size={24} />
          </div>
        ) : (
          <>
            {Object.entries(routine)
              .sort(([_, activityA], [__, activityB]) => {
                if (
                  !activityA.startTime ||
                  !activityB.startTime ||
                  !activityA.endTime ||
                  !activityB.endTime
                ) {
                  return 0;
                }
                const startTimeA = transformHHmmToDayjs(activityA.startTime).valueOf();
                const startTimeB = transformHHmmToDayjs(activityB.startTime).valueOf();

                // First sort by startTime
                if (startTimeA !== startTimeB) {
                  return startTimeA - startTimeB;
                }

                // If startTimes are the same, sort by endTime (earlier endTime first)
                const endTimeA = transformHHmmToDayjs(activityA.endTime).valueOf();
                const endTimeB = transformHHmmToDayjs(activityB.endTime).valueOf();
                return endTimeA - endTimeB;
              })
              .map(([activityId, activity]) => {
                const scheduledEvent = existingRoutineEvents.find(
                  (event) => event.autoScheduleFrom?.id === activityId
                );
                const duraitonFromScheduledEvent =
                  scheduledEvent?.end?.date &&
                  scheduledEvent?.start?.date &&
                  dayjs(scheduledEvent?.end?.date).diff(
                    dayjs(scheduledEvent?.start?.date),
                    'minute'
                  );

                const duration = duraitonFromScheduledEvent
                  ? translateMinToHHhMMmString(duraitonFromScheduledEvent)
                  : activity.durationMin
                    ? translateMinToHHhMMmString(activity.durationMin)
                    : '';
                const startTime =
                  scheduledEvent?.start?.date && dayjs(scheduledEvent.start.date).isValid()
                    ? formatTimeFromDayjs(dayjs(scheduledEvent.start.date), timeFormat)
                    : activity.startTime
                      ? formatTimeFromHHMM(activity.startTime, timeFormat)
                      : '';
                const endTime =
                  scheduledEvent?.end?.date && dayjs(scheduledEvent.end.date).isValid()
                    ? formatTimeFromDayjs(dayjs(scheduledEvent.end.date), timeFormat)
                    : activity.endTime
                      ? formatTimeFromHHMM(activity.endTime, timeFormat)
                      : '';
                const isAlreadyScheduled = scheduledRoutineActivityIds.has(activityId);

                return (
                  <SchedulerRoutineItem
                    key={activityId}
                    title={activity.name}
                    startTime={startTime}
                    endTime={endTime}
                    duration={duration}
                    selectable={true}
                    checked={activity.selected === true}
                    disabled={hasConfigured}
                    showScheduleIcon={isAlreadyScheduled}
                    onPress={() =>
                      handleClickRoutineActivity({
                        ...activity,
                        routineActivityId: activityId,
                        isAlreadyScheduled,
                      })
                    }
                    onCheckChange={(checked) => {
                      onRoutineCheckChange(activityId, checked);
                    }}
                  />
                );
              })}
            {!hasConfigured && (
              <Button
                variant="contained"
                onClick={handleAddRoutineActivity}
                className="scheduler-routine-chat-pref__add-button"
                startIcon={<AddIcon />}
              >
                Add Routine
              </Button>
            )}
            {isModalOpen && (
              <RoutineInfoModal
                isOpen={isModalOpen}
                onClose={() => {
                  setEditingRoutineItem(null);
                  setIsModalOpen(false);
                }}
                onSave={handleRoutineItemChange}
                mode={routineInfoMode}
                selectedDate={selectedDate}
                initialActivity={editingRoutineItem}
                onDelete={onRoutineDelete}
                isAlreadyScheduled={editingRoutineIsAlreadyScheduled}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SchedulerRoutineChatPref;
